import {
  useCreateProfile,
  useCreateUser,
  useTransferTicket,
} from '@sunrise-ui/api-client';
import type { Ticket } from '@sunrise-ui/api/events';
import type { Profile } from '@sunrise-ui/api/profile';
import {
  AuthFlowError,
  Button,
  Colors,
  TextBold,
  TextInput,
  TextReg,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { PersonSearch } from '../users/PersonSearch';

export type TicketTransferFormValues = {
  people: Array<{
    firstName: string;
    lastName: string;
    email: string;
  }>;
};

type Props = {
  ticket: Ticket;
  communityId: string;
};

export function TicketTransferForm({ ticket, communityId }: Props) {
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form fields for creating new user
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { mutateAsync: transferTicket } = useTransferTicket(
    ticket,
    communityId,
  );
  const { mutateAsync: createUser, isPending: isCreatingUser } =
    useCreateUser();
  const { mutateAsync: createProfile, isPending: isCreatingProfile } =
    useCreateProfile();

  const handleSelectProfile = (profile: Profile) => {
    setSelectedProfile(profile);
    setShowCreateForm(false);
  };

  const handleCreateNewUser = async () => {
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !password.trim()
    ) {
      return;
    }

    try {
      // Create user in Cerebro
      const user = await createUser({
        email: email.trim(),
        password: password.trim(),
      });

      // Create profile with first and last name
      const profile = await createProfile({
        user_id: user.id,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      // Set as selected profile and clear form
      setSelectedProfile(profile);
      setShowCreateForm(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
    } catch (error) {
      if (error instanceof AuthFlowError) return;
      console.error('Failed to create user:', error);
    }
  };

  const handleTransfer = async () => {
    if (!selectedProfile) return;

    setIsTransferring(true);
    try {
      await transferTicket({
        first_name: selectedProfile.first_name || '',
        last_name: selectedProfile.last_name || '',
        email: selectedProfile.user_id,
      });
      // Reset form after successful transfer
      setSelectedProfile(null);
    } finally {
      setIsTransferring(false);
    }
  };

  const isCreating = isCreatingUser || isCreatingProfile;

  return (
    <View style={styles.container}>
      {!showCreateForm ? (
        <>
          <PersonSearch
            onSelect={handleSelectProfile}
            placeholderLabel="Search for user to transfer ticket to..."
            minChars={2}
            profileResultSize="medium"
          />

          <TouchableOpacity
            onPress={() => setShowCreateForm(true)}
            style={styles.createUserButton}
          >
            <TextReg style={styles.createUserText}>
              + Can&apos;t find them? Create new user
            </TextReg>
          </TouchableOpacity>
        </>
      ) : (
        <View style={styles.createFormContainer}>
          <View style={styles.createFormHeader}>
            <TextBold style={styles.createFormTitle}>Create New User</TextBold>
            <TouchableOpacity onPress={() => setShowCreateForm(false)}>
              <TextReg style={styles.cancelLink}>Cancel</TextReg>
            </TouchableOpacity>
          </View>

          <TextInput
            label="First Name"
            value={firstName}
            onChange={setFirstName}
            autoCapitalize="words"
            style={styles.input}
          />
          <TextInput
            label="Last Name"
            value={lastName}
            onChange={setLastName}
            autoCapitalize="words"
            style={styles.input}
          />
          <TextInput
            label="Email"
            value={email}
            onChange={setEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            style={styles.input}
          />
          <TextInput
            label="Temporary Password"
            value={password}
            onChange={setPassword}
            secureTextEntry
            autoCapitalize="none"
            style={styles.input}
          />

          <Button
            onPress={handleCreateNewUser}
            disabled={
              !firstName.trim() ||
              !lastName.trim() ||
              !email.trim() ||
              !password.trim() ||
              isCreating
            }
            loading={isCreating}
            style={styles.createButton}
          >
            Create User
          </Button>
        </View>
      )}

      {selectedProfile && (
        <View style={styles.selectedUserContainer}>
          <TextBold style={styles.selectedUserLabel}>Transfer to:</TextBold>
          <TextReg style={styles.selectedUserText}>
            {selectedProfile.first_name} {selectedProfile.last_name}
          </TextReg>
          <TextReg style={styles.selectedUserEmail}>
            User ID: {selectedProfile.user_id}
          </TextReg>
        </View>
      )}

      <Button
        onPress={handleTransfer}
        disabled={!selectedProfile || isTransferring}
        loading={isTransferring}
        style={styles.transferButton}
      >
        Transfer Ticket
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: 24,
  },
  createUserButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  createUserText: {
    color: Colors.purple0,
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  createFormContainer: {
    gap: 16,
    padding: 16,
    backgroundColor: Colors.opacity05,
    borderRadius: 12,
  },
  createFormHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  createFormTitle: {
    fontSize: 16,
  },
  cancelLink: {
    color: Colors.purple0,
    fontSize: 14,
  },
  input: {
    backgroundColor: '#FFFFFF',
  },
  createButton: {
    marginTop: 8,
  },
  selectedUserContainer: {
    gap: 8,
  },
  selectedUserLabel: {
    fontSize: 16,
  },
  selectedUserText: {
    fontSize: 14,
  },
  selectedUserEmail: {
    fontSize: 14,
    opacity: 0.7,
  },
  transferButton: {
    marginTop: 8,
  },
});
